import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Database, Download, Upload, Trash2, RefreshCw, FileArchive,
  AlertTriangle, CheckCircle2, Loader2,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";

const SCOPES = [
  { v: "selective", l: "Selective — pick modules" },
  { v: "full", l: "Full database" },
  { v: "with_files", l: "Full + uploaded files (thumbnails, banners, certs)" },
];

const fmtSize = (n) => {
  if (!n) return "0 B";
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
};

export default function AdminBackupsPage() {
  const [rows, setRows] = useState(null);
  const [modules, setModules] = useState([]);
  const [scope, setScope] = useState("selective");
  const [selected, setSelected] = useState(new Set(["users", "courses"]));
  const [includeFrontend, setIncludeFrontend] = useState(false);
  const [includeBackend, setIncludeBackend] = useState(false);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [b, m] = await Promise.all([
        api.get("/admin/backups"),
        api.get("/admin/backups/modules/available"),
      ]);
      setRows(b.data.data?.backups || []);
      setModules(m.data.data?.modules || []);
    } catch (err) { toast.error(formatApiError(err)); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (k) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  };

  const create = async () => {
    setCreating(true);
    try {
      const q = new URLSearchParams();
      q.set("scope", scope);
      if (scope === "selective") q.set("modules", [...selected].join(","));
      if (includeFrontend) q.set("include_frontend", "true");
      if (includeBackend) q.set("include_backend", "true");
      if (name.trim()) q.set("backup_name", name.trim());
      const { data } = await api.post(`/admin/backups?${q.toString()}`);
      toast.success(`Backup ready — ${fmtSize(data.data?.file_size)}`);
      setName("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCreating(false); }
  };

  const download = async (row) => {
    setDownloading(row.id);
    try {
      const resp = await api.get(`/admin/backups/${row.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = row.backup_name || `backup-${row.id}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDownloading(null); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.backup_name}" from history? The zip in storage remains for now.`)) return;
    try {
      await api.delete(`/admin/backups/${row.id}`);
      toast.success("Backup entry deleted.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const restore = async (file, dryRun) => {
    setRestoring(true); setRestoreResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(
        `/admin/backups/restore?dry_run=${dryRun ? "true" : "false"}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setRestoreResult({ dryRun, ...data.data });
      toast.success(dryRun ? "Dry-run complete." : "Restore complete.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRestoring(false); }
  };

  const totals = useMemo(() => {
    if (!restoreResult) return null;
    const s = restoreResult.summary || {};
    let inserted = 0, skipped = 0, total = 0;
    for (const v of Object.values(s)) {
      inserted += (v.inserted || v.would_insert || 0);
      skipped += (v.skipped_existing || 0);
      total += (v.total || 0);
    }
    return { inserted, skipped, total };
  }, [restoreResult]);

  if (rows === null) return <DashboardLayout title="Backups"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Backup & Restore" breadcrumbs={["Super Admin", "Backups"]}>
      <div className="space-y-6" data-testid="admin-backups">
        {/* ── Create ─────────────────────────────────────── */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="backup-create-panel">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-[#101876]" />
            <h3 className="text-sm font-bold uppercase tracking-widest text-[#101876]">Create backup</h3>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Scope</label>
              <div className="mt-2 space-y-1.5">
                {SCOPES.map((s) => (
                  <label key={s.v} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs hover:bg-slate-50">
                    <input type="radio" name="scope" checked={scope === s.v} onChange={() => setScope(s.v)}
                           data-testid={`backup-scope-${s.v}`} />
                    <span>{s.l}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Modules {scope !== "selective" && <span className="ml-1 text-slate-400">(ignored for full)</span>}</label>
              <div className={`mt-2 grid grid-cols-2 gap-1.5 ${scope !== "selective" ? "opacity-40 pointer-events-none" : ""}`}>
                {modules.map((m) => (
                  <label key={m.key} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs hover:bg-slate-50">
                    <input type="checkbox" checked={selected.has(m.key)} onChange={() => toggle(m.key)}
                           data-testid={`backup-module-${m.key}`} />
                    <span className="capitalize">{m.key}</span>
                    <span className="ml-auto text-[10px] text-slate-400">{m.collections.length} col</span>
                  </label>
                ))}
              </div>
              <div className="mt-3">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Name (optional)</label>
                <input value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="e.g. pre-migration-oct"
                       data-testid="backup-name-input"
                       className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#101876]" />
              </div>
            </div>
          </div>
          {/* Include source code — completely independent of DB scope */}
          <div className="mt-4">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Also bundle source code</label>
            <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs hover:bg-slate-50">
                <input type="checkbox" checked={includeFrontend} onChange={(e) => setIncludeFrontend(e.target.checked)}
                       data-testid="backup-include-frontend" />
                <div className="min-w-0">
                  <div className="font-semibold">Frontend source</div>
                  <div className="text-[10px] text-slate-500">React app (src, public, configs) — skips node_modules, build</div>
                </div>
              </label>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs hover:bg-slate-50">
                <input type="checkbox" checked={includeBackend} onChange={(e) => setIncludeBackend(e.target.checked)}
                       data-testid="backup-include-backend" />
                <div className="min-w-0">
                  <div className="font-semibold">Backend source</div>
                  <div className="text-[10px] text-slate-500">FastAPI app (routers, tests, requirements) — skips __pycache__, .venv</div>
                </div>
              </label>
            </div>
          </div>
          <div className="mt-5 flex items-center gap-3">
            <button type="button" onClick={create}
                    disabled={creating || (scope === "selective" && selected.size === 0 && !includeFrontend && !includeBackend)}
                    data-testid="backup-create-btn"
                    className="inline-flex items-center gap-2 rounded-lg bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0a1155] disabled:opacity-50">
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileArchive className="h-3.5 w-3.5" />}
              {creating ? "Creating…" : "Create backup"}
            </button>
            <button type="button" onClick={load} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#101876]" data-testid="backup-refresh-btn">
              <RefreshCw className="h-3 w-3" /> Refresh
            </button>
          </div>
        </section>

        {/* ── Restore ────────────────────────────────────── */}
        <section className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5" data-testid="backup-restore-panel">
          <div className="flex items-center gap-2">
            <Upload className="h-4 w-4 text-amber-700" />
            <h3 className="text-sm font-bold uppercase tracking-widest text-amber-800">Restore from file</h3>
          </div>
          <div className="mt-2 flex items-start gap-2 text-[11px] text-amber-800">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <p>Documents whose <code>id</code> already exists will be <b>skipped</b> — nothing is overwritten. Try a dry-run first to preview.</p>
          </div>
          <input ref={fileRef} type="file" accept=".zip" className="hidden"
                 data-testid="backup-restore-file"
                 onChange={(e) => {
                   const f = e.target.files?.[0]; if (!f) return;
                   restore(f, true);   // default: dry-run so admins see the diff first
                   e.target.value = "";
                 }} />
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} disabled={restoring}
                    data-testid="backup-restore-dryrun-btn"
                    className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-4 py-2 text-xs font-bold text-amber-800 hover:bg-amber-100 disabled:opacity-50">
              {restoring ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              {restoring ? "Working…" : "Choose zip → dry-run preview"}
            </button>
            {restoreResult?.dryRun && (
              <button type="button" onClick={() => fileRef.current && fileRef.current.files?.[0] && restore(fileRef.current.files[0], false)}
                      data-testid="backup-restore-commit-btn"
                      className="inline-flex items-center gap-2 rounded-lg bg-amber-700 px-4 py-2 text-xs font-bold text-white hover:bg-amber-800">
                <CheckCircle2 className="h-3.5 w-3.5" /> Commit restore
              </button>
            )}
          </div>
          {restoreResult && (
            <div className="mt-4 rounded-xl border border-amber-200 bg-white p-3" data-testid="backup-restore-result">
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold">{restoreResult.dryRun ? "Dry-run summary" : "Restore result"}</span>
                {totals && (
                  <span className="text-slate-500">
                    <span className="font-bold text-green-700">{totals.inserted}</span> new · {" "}
                    <span className="font-bold text-slate-500">{totals.skipped}</span> skipped · {" "}
                    <span className="text-slate-400">of {totals.total}</span>
                  </span>
                )}
              </div>
              <div className="mt-3 max-h-60 overflow-y-auto text-[11px]">
                <table className="w-full">
                  <thead className="text-[10px] uppercase text-slate-400"><tr>
                    <th className="p-1 text-left">Collection</th>
                    <th className="p-1 text-right">In file</th>
                    <th className="p-1 text-right">New</th>
                    <th className="p-1 text-right">Skipped</th>
                  </tr></thead>
                  <tbody>
                    {Object.entries(restoreResult.summary || {}).map(([col, v]) => (
                      <tr key={col} className="border-t border-slate-100">
                        <td className="p-1 font-mono">{col}</td>
                        <td className="p-1 text-right">{v.total ?? "—"}</td>
                        <td className="p-1 text-right font-bold text-green-700">{v.inserted || v.would_insert || 0}</td>
                        <td className="p-1 text-right text-slate-500">{v.skipped_existing ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        {/* ── History ────────────────────────────────────── */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="backup-history-panel">
          <div className="flex items-center gap-2">
            <FileArchive className="h-4 w-4 text-[#329112]" />
            <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700">History ({rows.length})</h3>
          </div>
          <div className="mt-4 space-y-2">
            {rows.length === 0 ? (
              <EmptyState title="No backups yet" description="Create your first backup above." icon={FileArchive} />
            ) : rows.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/40 p-3" data-testid={`backup-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="text-xs font-bold">
                    {r.backup_name}
                    <span className={`ml-2 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ${
                      r.backup_type === "full" ? "bg-[#101876]/10 text-[#101876]" :
                      r.backup_type === "with_files" ? "bg-[#329112]/10 text-[#329112]" :
                      "bg-slate-200 text-slate-700"
                    }`}>{r.backup_type}</span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {new Date(r.created_at).toLocaleString()} · {fmtSize(r.file_size)} · {r.collections?.length || 0} collections
                    {r.files_included > 0 && <> · <span className="text-[#329112]">{r.files_included} storage files</span></>}
                    {r.frontend_files > 0 && <> · <span className="text-[#101876]">frontend ({r.frontend_files})</span></>}
                    {r.backend_files > 0 && <> · <span className="text-[#101876]">backend ({r.backend_files})</span></>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => download(r)} disabled={downloading === r.id}
                          data-testid={`backup-download-${r.id}`}
                          className="inline-flex items-center gap-1 rounded-lg bg-[#101876] px-3 py-1.5 text-[11px] font-bold text-white hover:bg-[#0a1155] disabled:opacity-50">
                    {downloading === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                    Download
                  </button>
                  <button type="button" onClick={() => remove(r)}
                          data-testid={`backup-delete-${r.id}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-[11px] font-bold text-rose-600 hover:bg-rose-50">
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
